/**
 * <ec-store-hours>
 *
 * Shows today's opening-hours sentence, or the exceptional-closure message
 * when today falls inside the configured closure period.
 *
 * Both the weekday and the date comparison are resolved here rather than in
 * Liquid because Shopify serves storefront HTML from its CDN cache: anything
 * computed from `'now'` at render time would keep showing Tuesday's hours on
 * Wednesday, or a holiday notice a month after the shop reopened.
 *
 * Two modes, told apart by the presence of data-hours:
 *  - bar mode: replaces a neutral server-rendered fallback with today's line;
 *  - notice mode: starts `hidden`, and only reveals itself during a closure.
 */

/**
 * Europe/Paris, not the visitor's zone: the opening day is a fact about the
 * shop in Voiron. A customer abroad, or one whose device clock sits on the
 * wrong side of midnight, must still read the day the shop is actually in.
 */
const STORE_TIME_ZONE = 'Europe/Paris';

/** Index order of Date#getDay(), used only if Intl has no time zone data. */
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @param {Date} date
 * @returns {string} lowercase English weekday name in the store's time zone
 */
export function storeWeekday(date) {
  try {
    // en-US long weekday names are stable ASCII, so they can key the payload.
    return new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TIME_ZONE,
      weekday: 'long',
    })
      .format(date)
      .toLowerCase();
  } catch {
    return DAY_KEYS[date.getDay()];
  }
}

/**
 * @param {Date} date
 * @returns {string} today in the store's time zone, as YYYY-MM-DD
 */
export function storeDate(date) {
  try {
    // formatToParts rather than a locale that "looks ISO": the part values are
    // specified, a locale's date order is not.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}

/**
 * A typo in the editor must not break the bar, so anything that is not a real
 * calendar date is dropped instead of being compared.
 *
 * @param {string | undefined} value
 * @returns {string | null}
 */
export function normalizeIsoDate(value) {
  const trimmed = (value || '').trim();
  if (!ISO_DATE.test(trimmed)) return null;
  const [year, month, day] = trimmed.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  // Rejects 2026-02-30 and friends, which Date would silently roll over.
  const roundTrips =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day;
  return roundTrips ? trimmed : null;
}

/**
 * ISO dates are zero-padded, so a plain string comparison orders them.
 * An open bound means "since forever" / "until further notice"; with both
 * bounds missing there is no period at all, so the message never shows.
 *
 * @param {{ start?: string, end?: string, message?: string }} closure
 * @param {Date} date
 * @returns {string | null} the message to display, or null
 */
export function closureMessageForDate(closure, date) {
  const message = (closure.message || '').trim();
  if (!message) return null;

  const start = normalizeIsoDate(closure.start);
  const end = normalizeIsoDate(closure.end);
  if (!start && !end) return null;

  const today = storeDate(date);
  if (start && today < start) return null;
  if (end && today > end) return null;
  return message;
}

/**
 * @param {Record<string, string>} sentences - weekday name → sentence
 * @param {Date} date
 * @returns {string | null} null when the payload has nothing for that day
 */
export function sentenceForDate(sentences, date) {
  return sentences[storeWeekday(date)] ?? null;
}

class EcStoreHours extends HTMLElement {
  /** @type {Record<string, string> | null} */
  #sentences = null;
  #onVisibility = null;

  connectedCallback() {
    const payload = this.dataset.hours;
    if (payload) {
      try {
        this.#sentences = JSON.parse(payload);
      } catch {
        // Malformed payload: keep the server-rendered fallback rather than
        // blanking the bar.
        return;
      }
    }

    this.#render();

    // A tab left open overnight would otherwise keep yesterday's line.
    this.#onVisibility = () => {
      if (!document.hidden) this.#render();
    };
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    if (this.#onVisibility) {
      document.removeEventListener('visibilitychange', this.#onVisibility);
      this.#onVisibility = null;
    }
  }

  #render() {
    const now = new Date();
    const closure = closureMessageForDate(
      {
        start: this.dataset.closureStart,
        end: this.dataset.closureEnd,
        message: this.dataset.closureMessage,
      },
      now
    );

    if (!this.#sentences) {
      // Notice mode: nothing to say outside the closure period.
      this.hidden = closure === null;
      if (closure !== null && closure !== this.textContent) this.textContent = closure;
      return;
    }

    const text = closure ?? sentenceForDate(this.#sentences, now);
    if (text && text !== this.textContent) this.textContent = text;
  }
}

if (!customElements.get('ec-store-hours')) {
  customElements.define('ec-store-hours', EcStoreHours);
}
